import { makeAutoObservable, runInAction } from 'mobx'
import { prepareSearchRegex } from 'shared/lib/prepareSearch'

import { logger } from 'shared/lib'
import { CustomField } from '../model'
import { CustomFieldApi, type IParamsCustomFields, type IResponseCustomFields } from '../api'

export class CustomFieldsStore {
  private _page = 0
  private _total = 0
  private _limit = 100

  private _loading = true
  private _search = ''
  private _fieldsMap: Map<string, CustomField> = new Map()

  constructor(limit = 100) {
    this._limit = limit

    makeAutoObservable(this)
  }

  get loading() {
    return this._loading
  }

  get fields() {
    return Array.from(this._fieldsMap.values())
  }

  get termFields() {
    if (!this._search) return this.fields

    const searchRegex = prepareSearchRegex(this._search)

    return this.fields.filter((field) =>
      field.searchFragments.some((fragment) => searchRegex.test(fragment))
    )
  }

  loadData = async (params?: IParamsCustomFields) => {
    this._loading = true

    try {
      const storeParams: IParamsCustomFields = {
        length: this._limit,
        page: this._page + 1,
      }
      const { data } = await CustomFieldApi.getCustomFields(params || storeParams)

      this._addData(data)

      runInAction(() => {
        this._loading = false
      })
    } catch (error) {
      logger.error(error)
    }
  }

  reloadData = async () => {
    this._loading = true

    try {
      const params = {
        length: this._limit,
        page: 1,
      }
      const { data } = await CustomFieldApi.getCustomFields(params)

      this._setData(data)

      runInAction(() => {
        this._loading = false
      })
    } catch (error) {
      logger.error(error)
    }
  }

  searchFields(search: string) {
    this._search = search.trim()
  }

  clearSearch() {
    this._search = ''
  }

  private _addData = (response: IResponseCustomFields) => {
    if ('data' in response) {
      const { data, total, current_page } = response

      data.forEach((item) => {
        this._fieldsMap.set(item.key, new CustomField(item))
      })

      this._page = current_page
      this._total = total
    }
    if (Array.isArray(response)) {
      response.forEach((item) => {
        this._fieldsMap.set(item.key, new CustomField(item))
      })
    }
  }

  private _setData = (response: IResponseCustomFields) => {
    this._fieldsMap.clear()

    if ('data' in response) {
      const { data, total, current_page } = response
      data.forEach((item) => {
        this._fieldsMap.set(item.key, new CustomField(item))
      })

      this._page = current_page
      this._total = total
    }
    if (Array.isArray(response)) {
      response.forEach((item) => {
        this._fieldsMap.set(item.key, new CustomField(item))
      })
    }
  }
}
